import React, { useContext, useEffect, useState } from "react";
import useAxios from "../../../../utils/useAxios";
import AuthContext from "../../../../context/AuthContext";
import Select from "react-select";
import OptionFormatter from "../../../../components/OptionFormatter";
import { useLicensorContext } from "../../../../context/LicensorContext";
import {
  formatLicensorIntoOption,
  getLicensors,
} from "../../../../helpers/licensor.helper";
import useLicensedProperty, {
  fetchLicensedProperty,
} from "../../../../hooks/useLicensedProperty";
import { useMasterArtworkCollectionStore } from "../../../../context/MasterArtworkCollectionContext";
import { MasterArtworkCollectionActions } from "../../../../states/meta-artwork/collections";
import {
  formatLicensedPropertyIntoOption,
  getLicensedPropertys,
} from "../../../../helpers/licensed-property.helper";
import {
  DebounceContext,
  DebounceContextType,
} from "../../../../context/DebounceContext";
import { Predicates } from "../../../../libraries/predicates/predicates";
import AsyncSelect from "react-select/async";
import useGlobalProduct, {
  fetchGlobalProduct,
} from "../../../../hooks/useGlobalProduct";
import {
  getGlobalProduct,
  getGlobalProducts,
} from "../../../../helpers/global-product.helper";
import ArtworkCollectionActionButtonsGroup from "../../components/collection-action-buttons/ArtworkCollectionActionButtons";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Spinner from "../../../../components/Spinner";
import { getTodayDate } from "../../../skus/features/sku-create/sku-create.helper";
import {
  artworkDesignGlobalPermissions,
  artworkMasterDataGlobalPermissions,
} from "../../helpers/artworkPermissions";
import isReadOnly from "routes/master-artwork/helpers/isReadOnlyHelper";
import Banner from "../../../../components/Banner";
import ArtworkRequestHistoryTable from "../../components/ArtworkRequestHistoryComponent";
import useArtworkCollection from "../../hooks/useArtworkCollectionHook";
import useArtworkGenericRequests from "../../hooks/useArtworkGenericReaquestsHook";

export const ArtworkMasterCollectionCreatePage = (props: { title: string }) => {
  const axios = useAxios();
  const authContext = useContext(AuthContext);
  const username = authContext?.username;
  const { id, code } = useParams();
  const location = useLocation()

  const hasCreatorPermission = artworkDesignGlobalPermissions.every(
    (permission) => authContext?.hasPermission(permission),
  );
  const hasApproverPermission = artworkMasterDataGlobalPermissions.every(
    (permission) => authContext?.hasPermission(permission),
  );

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState<boolean>(
    () => !!urlSearchParams.get("editing") && hasApproverPermission,
  );

  const { searchDebounce } = useContext<DebounceContextType>(DebounceContext);
  const { state, dispatch } = useMasterArtworkCollectionStore();

  const { data: licensors, isLoading: isLicensorsLoading } =
    useLicensorContext();

  const { data: licensedProperties, isLoading: isLicensedPropertiesLoading } =
    useLicensedProperty({
      licensorId: String(state.licensor?.id?.toString() ?? ""),
    });

  const licensedPropertiesOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3)
      return getLicensedPropertys(licensedProperties);
    const response = await fetchLicensedProperty({
      licensorId: String(String(state.licensor?.id?.toString() ?? "")),
      search,
      axios,
    });
    callback(getLicensedPropertys(response));
  };
  const [codeAlreadyExists, setCodeAlreadyExists] = useState<boolean>(false);

  const { data: collectionsData, isLoading: collectionsIsLoading } =
    useArtworkCollection({ search: `code=${state.code}` });

  const { data: globalProducts, isLoading: isGlobalProductsLoading } =
    useGlobalProduct({
      category: undefined,
      sub_category: undefined,
      product_line: undefined,
    });

  const [gpTaken, setGPTaken] = useState<any[]>([]);

  const getGlobalProductById = async (id: any) => {
    try {
      const response = await axios.get(`/artwork-master/global-products/${id}`);
      if (response.status !== 404) {
        return response;
      }
    } catch (error) {
      return null;
    }
  };

  const getAllGPTaken = async () => {
    const response = await axios.get(`/artwork-master/global-products/`);
    if (response.status !== 404) {
      setGPTaken(response.data);
    }
  }

  const validateGlobalProduct = async () => {
    let isTaken = [];

    for (const gp of state.global_product || []) {
      const globalProducts = await getGlobalProductById(gp.id);
      if (globalProducts?.data) {
        isTaken.push(globalProducts.data);
      }
    }
    setGPTaken(isTaken);
  };

  const loadGlobalProducts = async (input: string, callback: any) => {
    let options: any = getGlobalProduct(globalProducts, {});
    if (input.length > 2) {
      const response = await fetchGlobalProduct({ search: input, axios });
      options = getGlobalProduct(response, {});
    }
    callback(options);
  };

  const [isSaveOrSubmitInProgress, setIsSaveOrSubmitInProgress] =
    useState<boolean>(false);

  const getCollectionRequest = async (id: string | number) => {
    const response = await axios.get(`/request/${id}`);
    const requestStatus = response.data.status;
    const isReadOnlyValue = isReadOnly(
      response.data.status,
      hasApproverPermission,
    );

    dispatch(
      MasterArtworkCollectionActions.setCollection({
        ...state,
        requestStatus: requestStatus,
        ...response.data.data,
        requestId: response.data.id,
        isReadOnly: isReadOnlyValue,
      }),
    );

    setIsLoading(false);
  };

  const [historyRecords, setHistoryRecords] = useState<any[]>([]);

  const getRequestHistory = async (id: string | number) => {
    const response = await axios.get(`/request-history/?request__id=${id}`);

    // @ts-ignore
    setHistoryRecords(response.data);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      getCollectionRequest(id);
      getRequestHistory(id);
      return;
    }
    dispatch(MasterArtworkCollectionActions.resetState());
    setHistoryRecords([]);
  }, [id, location]);

  const getMissingConditionsMessage = () => {
    const levelRequired = "full";

    const conditions = [
      {
        condition: (state: any) =>
          state.name.length > 3 &&
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(state.name),
        message: `<strong>Name</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          /^[A-Za-z0-9]{4}_[A-Za-z0-9]{3}$/.test(state.code),
        message: `<strong>Must have a code in that format AAAA_AAA, alphanumeric</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) => !codeAlreadyExists,
        message: `<strong>Collection code Already exists</strong>`,
        conditionLevel: "full",
      },
      // {
      //   condition: (state: any) => !(gpTaken.length > 0),
      //   message: `<strong>Collection code those global products already been taken ${gpTaken.map((gp) => gp.name)}</strong>`,
      //   conditionLevel: "full",
      // },
    ];

    const missingConditions = conditions.filter(
      (condition) =>
        (levelRequired === "full"
          ? true
          : condition.conditionLevel === levelRequired) &&
        !condition.condition(state),
    );

    if (missingConditions.length < 1) {
      return "";
    }

    let message =
      "You cannot proceed to collection creation. The following requirements are missing <br> <ul class='mb-0'>";
    message += missingConditions
      .map((condition) => `<li>${condition.message}</li>`)
      .join("");
    return message + "</ul>";
  };

  useEffect(() => {
    const isValid = !!getMissingConditionsMessage() || codeAlreadyExists;

    dispatch(MasterArtworkCollectionActions.setIsValid(!isValid));

    if (state.requestStatus === null || state.requestStatus === "") {
      dispatch(MasterArtworkCollectionActions.setRequestStatus("CREATED"));
    }
  }, [state.name, state.code, state.global_product]);

  const verifyIfCodeAlreadyExists = async (requestCollectionCode: string) => {
    try {
      setIsSaveOrSubmitInProgress(true);

      if (!code) {
        dispatch(MasterArtworkCollectionActions.setIsValid(false));

        try {
          const [responseRequests, responseCollections] = await Promise.all([
            axios.get(
              `artwork-master/request/?search=${requestCollectionCode}&context=2`,
            ),
            hasApproverPermission
              ? axios.get(
                  `/artwork-master/collections/?search=${requestCollectionCode}`,
                )
              : Promise.resolve({ data: [] }),
          ]);

          const requestCodeExists =
            responseRequests.data.length > 0 &&
            responseRequests.data[0].id !== Number(id);

          const collectionCodeExists =
            hasApproverPermission && responseCollections.data.length > 0;

          setCodeAlreadyExists(requestCodeExists || collectionCodeExists);
          dispatch(
            MasterArtworkCollectionActions.setIsValid(
              !(requestCodeExists || collectionCodeExists),
            ),
          );
        } catch (error) {
          console.error("Error checking code existence:", error);
        }
      }

      if (code) {
        try {
          const responseRequests = await axios.get(
            `artwork-master/request/?search=${requestCollectionCode}&context=2`,
          );

          const codeExists =
            responseRequests.data.length > 0 && state.code !== code;

          setCodeAlreadyExists(codeExists);
          dispatch(MasterArtworkCollectionActions.setIsValid(!codeExists));
        } catch (error) {
          console.error("Error checking code existence:", error);
        }
      }
    } finally {
      setIsSaveOrSubmitInProgress(false);
    }
  };

  useEffect(() => {
    if (state.code.length > 7) {
      verifyIfCodeAlreadyExists(state.code);
    }
  }, [id, state.code, location]);

  const [searchParams, setSearchParams] = useState<any>({
    limit: 1,
    lastDays: -1,
    requestor: "",
    context: [2],
    status: ["APPROVED"],
    search: code,
    hasFilterApplyed: false,
    offset: 0,
  });

  const { data: collection, isLoading: collectionIsLoading } =
    useArtworkGenericRequests({ ...searchParams });

  useEffect(() => {
    if (code) {
      setIsLoading(true);

      //@ts-ignore
      if (collection) {
        //@ts-ignore
        const dataResult = collection.results[0];
        const collDTO = {
          ...dataResult.data,
        };

        //@ts-ignore
        dispatch(
          MasterArtworkCollectionActions.setCollection({
            ...collDTO,
            requestStatus: dataResult.status,
            requestId: dataResult.id,
          }),
        );
        dispatch(MasterArtworkCollectionActions.setIsReadOnly(!isEditing));

        setHistoryRecords(collDTO.requestHistory);
      }
      setIsLoading(collectionIsLoading);
    }
  }, [collection, code]);

  useEffect(() => {
    if (code) {
      dispatch(MasterArtworkCollectionActions.setIsReadOnly(!isEditing));
    }
  }, [isEditing]);

  useEffect(() => {
    validateGlobalProduct();
    getAllGPTaken();
  }, [state.global_product]);


  // @ts-ignore
  return (
    <>
      {isLoading && <Spinner />}
      {!isLoading && (
        <div
          className="container-fluid mt-3 bg-white shadow py-3 rounded-1 d-flex flex-column justify-contnet-between h-100"
          style={{ minHeight: "70vh" }}
        >
          <h6 className="text-primary fw-bold card-header border-0 mb-2">
            {/*Create new Collection*/}
            {props.title}
          </h6>

          <div>
            <div className="row form-row align-items-end mb-0">
              <div className="col-lg-6 form-group">
                <label className="form-label">Collection Name*</label>
                <input
                  onChange={(e) =>
                    dispatch(
                      MasterArtworkCollectionActions.setName(e.target.value),
                    )
                  }
                  value={state.name}
                  type="text"
                  className="form-control d-inline"
                  disabled={state.isReadOnly}
                />
              </div>
              <div className="col-lg-6 form-group">
                <label className="form-label">Collection Code*</label>
                <input
                  onChange={(e) =>
                    dispatch(
                      MasterArtworkCollectionActions.setCode(e.target.value),
                    )
                  }
                  onKeyPress={(e) => {
                    const regex = /^[A-Za-z0-9_]$/;
                    if (!regex.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onBlur={() => {
                    getMissingConditionsMessage();
                  }}
                  type="text"
                  maxLength={8}
                  className="form-control d-inline"
                  disabled={state.isReadOnly}
                  value={state.code}
                  placeholder="Max 8 characteres (ex: HDTB_SWT)"
                />
              </div>
              <div className="col-lg-6 form-group">
                <label className="form-label">Collection Licensor</label>
                <Select
                  onChange={(e) =>
                    dispatch(
                      MasterArtworkCollectionActions.setLicensor(
                        e?.value ?? null,
                      ),
                    )
                  }
                  value={formatLicensorIntoOption(state.licensor)}
                  formatOptionLabel={OptionFormatter}
                  options={getLicensors(licensors)}
                  isClearable={true}
                  classNamePrefix="react-select"
                  isDisabled={state.isReadOnly}
                />
              </div>
              <div className="col-lg-6 form-group">
                <label className="form-label">Collection License</label>
                <AsyncSelect
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      licensedPropertiesOptions,
                      input,
                      getLicensedPropertys(licensedProperties),
                      callback,
                    );
                  }}
                  defaultOptions={getLicensedPropertys(licensedProperties)}
                  onChange={(e) =>
                    dispatch(
                      MasterArtworkCollectionActions.setLicensedProperty(
                        e?.value ?? null,
                      ),
                    )
                  }
                  formatOptionLabel={OptionFormatter}
                  value={formatLicensedPropertyIntoOption(
                    state.licensed_property,
                  )}
                  classNamePrefix="react-select"
                  placeholder=""
                  isClearable
                  isLoading={isLicensedPropertiesLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={state.isReadOnly || !state.licensor}
                />
              </div>
              <div className="col-lg-6 form-group">
                <label className="form-label">Collection Launch Date</label>
                <input
                  onChange={(e) =>
                    dispatch(
                      MasterArtworkCollectionActions.setLaunchDate(
                        e.target.value,
                      ),
                    )
                  }
                  value={state.launch_date}
                  min={getTodayDate()}
                  type="date"
                  className="form-control d-inline"
                  disabled={state.isReadOnly}
                />
              </div>
              <div className="col-lg-6 form-group">
                <label className="form-label">
                  Global Product Code / Name*
                </label>
                <AsyncSelect
                  name="globalProduct"
                  value={getGlobalProducts(state.global_product ?? [])}
                  onChange={(e) =>
                    dispatch(
                      MasterArtworkCollectionActions.setGlobalProduct(
                        e.map(
                          (globalProductOption: any) =>
                            globalProductOption.value,
                        ),
                      ),
                    )
                  }
                  // defaultOptions={getGlobalProduct(globalProducts, {})}
                  defaultOptions={getGlobalProduct(
                    globalProducts.filter(item =>
                      !gpTaken.some(toRemove =>
                        toRemove.id === item.id
                      )
                    ),
                    {},
                  )}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      loadGlobalProducts,
                      input,
                      getGlobalProduct(globalProducts, {}),
                      callback,
                    );
                  }}
                  isMulti
                  formatOptionLabel={OptionFormatter}
                  isLoading={isGlobalProductsLoading}
                  isClearable
                  isSearchable
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                  isDisabled={state.isReadOnly}
                />
              </div>
            </div>
          </div>

          {historyRecords.length > 0 && (
            <ArtworkRequestHistoryTable historyRecords={historyRecords} />
          )}

          {!state.isValid && !state.isReadOnly && (
            <>
              <Banner
                classname={"mt-3"}
                message={getMissingConditionsMessage()}
                type="danger"
              />
            </>
          )}

          <ArtworkCollectionActionButtonsGroup
            //@ts-ignore
            isReadOnlyRequest={state.isReadOnly}
            isEditing={Predicates.isNotNullAndNotUndefinedAndNotEmpty(
              isEditing,
            )}
            isSaveOrSubmitInProgress={isSaveOrSubmitInProgress}
            username={username ?? ""}
            hasApproverPermission={hasApproverPermission}
            hasCreatorPermission={hasCreatorPermission}
            setIsSaveOrSubmitInProgress={setIsSaveOrSubmitInProgress}
          />
        </div>
      )}
    </>
  );
};
